import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { woocommerceAPI, type WooCommerceCoupon } from "@/lib/woocommerce";
import { Loader2, Trash2, RefreshCw, Calendar, Percent, Tag, Gift } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const schema = z.object({
  code: z.string().min(1, "Obrigatório"),
  category: z.enum(["influencer", "nutricionista", "revendedor", "teste"], {
    required_error: "Selecione uma categoria",
  }),
});

type FormValues = z.infer<typeof schema>;
type CouponCategory = "influencer" | "nutricionista" | "revendedor" | "teste";

interface CouponWithCategory extends WooCommerceCoupon {
  category?: CouponCategory;
  baseCode?: string;
}

export const AdminCreateCoupon: React.FC = () => {
  const [recentCoupons, setRecentCoupons] = useState<CouponWithCategory[]>([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [deletingCoupon, setDeletingCoupon] = useState<number | null>(null);
  const [changeCategoryDialog, setChangeCategoryDialog] = useState<{
    open: boolean;
    coupon?: CouponWithCategory;
    newCategory?: CouponCategory;
  }>({ open: false });

  const form = useForm<FormValues>({
    // Type cast to work around Resolver type mismatch between packages in our setup
    resolver: zodResolver(schema) as unknown as import("react-hook-form").Resolver<FormValues>,
    defaultValues: {
      code: "",
      category: "influencer",
    },
  });

  const createCouponSafe = async (payload: Record<string, unknown>) => {
    try {
      const created = await woocommerceAPI.createCoupon(payload);
      return { ok: true as const, created };
    } catch (e) {
      return { ok: false as const, error: e };
    }
  };

  // Load recent coupons
  const loadRecentCoupons = async () => {
    setLoadingCoupons(true);
    try {
      const coupons = await woocommerceAPI.getCoupons({
        per_page: 50,
        orderby: "date",
        order: "desc",
      });
      
      // Group coupons by base code and detect categories
      const couponsWithCategory = coupons.map(coupon => {
        const code = coupon.code.toUpperCase();
        const baseCode = code.endsWith("PEDIDO") ? code.slice(0, -6) : code;
        
        // Detect category based on discount patterns
        let category: CouponCategory | undefined;
        
        if (coupon.discount_type === "percent") {
          if (coupon.amount === "15" || coupon.amount === "15.00") {
            // Could be influencer, nutricionista, revendedor, or teste — check PEDIDO coupon
            const hasPedidoCoupon = coupons.find(c =>
              c.code.toUpperCase() === `${baseCode}PEDIDO`
            );
            if (hasPedidoCoupon) {
              if (hasPedidoCoupon.discount_type === "fixed_cart" && (hasPedidoCoupon.amount === "600" || hasPedidoCoupon.amount === "600.00")) {
                category = "influencer";
              } else if (hasPedidoCoupon.discount_type === "fixed_cart" && (hasPedidoCoupon.amount === "400" || hasPedidoCoupon.amount === "400.00")) {
                category = "teste";
              } else if (hasPedidoCoupon.discount_type === "percent" && (hasPedidoCoupon.amount === "20" || hasPedidoCoupon.amount === "20.00")) {
                category = "revendedor";
              } else if (hasPedidoCoupon.discount_type === "percent" && (hasPedidoCoupon.amount === "25" || hasPedidoCoupon.amount === "25.00")) {
                category = "nutricionista";
              }
            }
          } else if ((coupon.amount === "20" || coupon.amount === "20.00") && !code.endsWith("PEDIDO")) {
            // Legacy nutricionista (20% base) — still detect for existing coupons
            category = "nutricionista";
          } else if ((coupon.amount === "20" || coupon.amount === "20.00") && code.endsWith("PEDIDO")) {
            category = "revendedor";
          } else if ((coupon.amount === "25" || coupon.amount === "25.00") && code.endsWith("PEDIDO")) {
            category = "nutricionista";
          }
        } else if (coupon.discount_type === "fixed_cart") {
          if (coupon.amount === "600" || coupon.amount === "600.00") {
            category = "influencer";
          } else if (coupon.amount === "400" || coupon.amount === "400.00") {
            category = "teste";
          }
        }
        
        return {
          ...coupon,
          baseCode,
          category,
        };
      });
      
      setRecentCoupons(couponsWithCategory);
    } catch (error) {
      console.error("Error loading coupons:", error);
      toast.error("Erro ao carregar cupons");
    } finally {
      setLoadingCoupons(false);
    }
  };

  useEffect(() => {
    loadRecentCoupons();
  }, []);

  const onSubmit = async (values: FormValues) => {
    const baseCode = values.code.trim().toUpperCase();
    if (!baseCode) {
      toast.error("Informe um código válido");
      return;
    }

    const tasks: Array<Promise<{ ok: true; created: WooCommerceCoupon } | { ok: false; error: unknown }>> = [];

    if (values.category === "influencer") {
      // 15% público ilimitado
      tasks.push(
        createCouponSafe({
          code: baseCode,
          discount_type: "percent",
          amount: "15",
          individual_use: true,
          exclude_sale_items: false,
        })
      );
      // R$600 pedido mensal (CODE+PEDIDO)
      tasks.push(
        createCouponSafe({
          code: `${baseCode}PEDIDO`,
          discount_type: "fixed_cart",
          amount: "600",
          usage_limit: 1,
          individual_use: true,
        })
      );
    } else if (values.category === "nutricionista") {
      // 15% seguidores (ilimitado)
      tasks.push(
        createCouponSafe({
          code: baseCode,
          discount_type: "percent",
          amount: "15",
          individual_use: true,
          exclude_sale_items: false,
        })
      );
      // 25% compra própria mensal (CODE+PEDIDO)
      tasks.push(
        createCouponSafe({
          code: `${baseCode}PEDIDO`,
          discount_type: "percent",
          amount: "25",
          usage_limit: 1,
          individual_use: true,
        })
      );
    } else if (values.category === "revendedor") {
      // 20% compra própria mensal (CODE+PEDIDO)
      tasks.push(
        createCouponSafe({
          code: `${baseCode}PEDIDO`,
          discount_type: "percent",
          amount: "20",
          usage_limit: 1,
          individual_use: true,
        })
      );
      // 15% seguidores ilimitado
      tasks.push(
        createCouponSafe({
          code: baseCode,
          discount_type: "percent",
          amount: "15",
          individual_use: true,
          exclude_sale_items: false,
        })
      );
    } else if (values.category === "teste") {
      // 15% seguidores ilimitado
      tasks.push(
        createCouponSafe({
          code: baseCode,
          discount_type: "percent",
          amount: "15",
          individual_use: true,
          exclude_sale_items: false,
        })
      );
      // R$400 pedido mensal (CODE+PEDIDO)
      tasks.push(
        createCouponSafe({
          code: `${baseCode}PEDIDO`,
          discount_type: "fixed_cart",
          amount: "400",
          usage_limit: 1,
          individual_use: true,
        })
      );
    }

    const results = [] as Array<{ label: string; ok: boolean }>;

    for (const t of tasks) {
      const r = await t;
      results.push({ label: r.ok ? (r as any).created?.code ?? baseCode : "erro", ok: r.ok });
    }

    const successCount = results.filter((r) => r.ok).length;

    results.forEach((r) => {
      if (r.ok) toast.success(`Criado: ${r.label}`);
      else toast.error("Falha ao criar um cupom");
    });

    if (successCount > 0) {
      form.reset({ code: "", category: values.category });
      loadRecentCoupons(); // Reload to show newly created coupons
    }
  };

  // Delete coupon
  const handleDeleteCoupon = async (coupon: CouponWithCategory) => {
    if (!confirm(`Tem certeza que deseja excluir o cupom ${coupon.code}?`)) {
      return;
    }

    setDeletingCoupon(coupon.id);
    try {
      await woocommerceAPI.deleteCoupon(coupon.id);
      toast.success(`Cupom ${coupon.code} excluído com sucesso!`);
      loadRecentCoupons();
    } catch (error) {
      console.error("Error deleting coupon:", error);
      toast.error(`Erro ao excluir cupom ${coupon.code}`);
    } finally {
      setDeletingCoupon(null);
    }
  };

  // Change category - delete old and create new
  const handleChangeCategory = async () => {
    const { coupon, newCategory } = changeCategoryDialog;
    if (!coupon || !newCategory || !coupon.baseCode) return;

    const baseCode = coupon.baseCode;
    
    try {
      // Find all coupons with the same base code
      const relatedCoupons = recentCoupons.filter(c => 
        c.baseCode === baseCode
      );

      // Delete all related coupons
      toast.info(`Excluindo ${relatedCoupons.length} cupom(ns) relacionados...`);
      for (const c of relatedCoupons) {
        await woocommerceAPI.deleteCoupon(c.id);
      }

      // Create new coupons with the new category
      toast.info(`Criando cupons na nova categoria...`);
      const tasks: Array<Promise<{ ok: true; created: WooCommerceCoupon } | { ok: false; error: unknown }>> = [];

      if (newCategory === "influencer") {
        tasks.push(
          createCouponSafe({
            code: baseCode,
            discount_type: "percent",
            amount: "15",
            individual_use: true,
            exclude_sale_items: false,
          })
        );
        tasks.push(
          createCouponSafe({
            code: `${baseCode}PEDIDO`,
            discount_type: "fixed_cart",
            amount: "600",
            usage_limit: 1,
            individual_use: true,
          })
        );
      } else if (newCategory === "nutricionista") {
        tasks.push(
          createCouponSafe({
            code: baseCode,
            discount_type: "percent",
            amount: "15",
            individual_use: true,
            exclude_sale_items: false,
          })
        );
        tasks.push(
          createCouponSafe({
            code: `${baseCode}PEDIDO`,
            discount_type: "percent",
            amount: "25",
            usage_limit: 1,
            individual_use: true,
          })
        );
      } else if (newCategory === "revendedor") {
        tasks.push(
          createCouponSafe({
            code: `${baseCode}PEDIDO`,
            discount_type: "percent",
            amount: "20",
            usage_limit: 1,
            individual_use: true,
          })
        );
        tasks.push(
          createCouponSafe({
            code: baseCode,
            discount_type: "percent",
            amount: "15",
            individual_use: true,
            exclude_sale_items: false,
          })
        );
      } else if (newCategory === "teste") {
        tasks.push(
          createCouponSafe({
            code: baseCode,
            discount_type: "percent",
            amount: "15",
            individual_use: true,
            exclude_sale_items: false,
          })
        );
        tasks.push(
          createCouponSafe({
            code: `${baseCode}PEDIDO`,
            discount_type: "fixed_cart",
            amount: "400",
            usage_limit: 1,
            individual_use: true,
          })
        );
      }

      const results = await Promise.all(tasks);
      const successCount = results.filter(r => r.ok).length;

      if (successCount === tasks.length) {
        toast.success(`Categoria alterada com sucesso! ${successCount} cupom(ns) criado(s).`);
      } else {
        toast.warning(`Categoria alterada parcialmente. ${successCount}/${tasks.length} cupom(ns) criado(s).`);
      }

      setChangeCategoryDialog({ open: false });
      loadRecentCoupons();
    } catch (error) {
      console.error("Error changing category:", error);
      toast.error("Erro ao alterar categoria");
    }
  };

  const getCategoryBadge = (category?: CouponCategory) => {
    if (!category) return <Badge variant="secondary">Desconhecida</Badge>;
    
    const styles = {
      influencer: { label: "Influenciadora", className: "bg-purple-500/20 text-purple-300 border border-purple-500/30" },
      nutricionista: { label: "Nutricionista", className: "bg-green-500/20 text-green-300 border border-green-500/30" },
      revendedor: { label: "Revendedor", className: "bg-blue-500/20 text-blue-300 border border-blue-500/30" },
      teste: { label: "Teste", className: "bg-amber-500/20 text-amber-300 border border-amber-500/30" },
    };

    const style = styles[category];
    return (
      <Badge variant="secondary" className={style.className}>
        {style.label}
      </Badge>
    );
  };

  // Group coupons by base code for display
  const groupedCoupons = recentCoupons.reduce((acc, coupon) => {
    const baseCode = coupon.baseCode || coupon.code;
    if (!acc[baseCode]) {
      acc[baseCode] = [];
    }
    acc[baseCode].push(coupon);
    return acc;
  }, {} as Record<string, CouponWithCategory[]>);

  const sortedBaseCodes = Object.keys(groupedCoupons).sort((a, b) => {
    const aCoupons = groupedCoupons[a];
    const bCoupons = groupedCoupons[b];
    const aDate = new Date(aCoupons[0].date_created).getTime();
    const bDate = new Date(bCoupons[0].date_created).getTime();
    return bDate - aDate;
  });

  return (
    <div className="space-y-6">
    <div className="flex items-center gap-3 mb-2">
      <Gift className="h-6 w-6 text-primary" />
      <h2 className="text-xl font-bold">Cupons</h2>
    </div>

    <Card>
      <CardContent className="pt-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Código</FormLabel>
                  <FormControl>
                    <Input placeholder="ex: TINA" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Categoria</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="influencer">Influenciadora oficial</SelectItem>
                      <SelectItem value="nutricionista">Nutricionista</SelectItem>
                      <SelectItem value="revendedor">Micro/Mano e Revendedores</SelectItem>
                      <SelectItem value="teste">Teste</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-2">
              <Button type="submit" className="flex-1">Criar</Button>
              <Button type="button" variant="outline" onClick={() => form.reset()} className="border-white/10 bg-white/5 hover:bg-white/10">
                Limpar
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>

      {/* Recent Coupons */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Cupons Recentes</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={loadRecentCoupons}
              disabled={loadingCoupons}
              className="border-white/10 bg-white/5 hover:bg-white/10"
            >
              {loadingCoupons ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              <span className="ml-2">Atualizar</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingCoupons ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : sortedBaseCodes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Tag className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum cupom encontrado</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedBaseCodes.slice(0, 20).map(baseCode => {
                const coupons = groupedCoupons[baseCode];
                const category = coupons[0].category;
                const createdDate = new Date(coupons[0].date_created);

                return (
                  <div
                    key={baseCode}
                    className="border border-white/5 rounded-lg p-4 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-lg">{baseCode}</h3>
                            {getCategoryBadge(category)}
                          </div>
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {format(createdDate, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {category && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setChangeCategoryDialog({
                                open: true,
                                coupon: coupons[0],
                              })
                            }
                            className="border-white/10 bg-white/5 hover:bg-white/10"
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Alterar
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      {coupons.map(coupon => (
                        <div
                          key={coupon.id}
                          className="flex items-center justify-between py-2 px-3 bg-white/5 rounded text-sm"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <code className="font-mono font-semibold">{coupon.code}</code>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Percent className="h-3 w-3" />
                              {coupon.discount_type === "percent"
                                ? `${coupon.amount}%`
                                : `R$ ${coupon.amount}`}
                            </div>
                            {coupon.usage_count > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                {coupon.usage_count} uso(s)
                              </Badge>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCoupon(coupon)}
                            disabled={deletingCoupon === coupon.id}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50"
                          >
                            {deletingCoupon === coupon.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Category Dialog */}
      <Dialog
        open={changeCategoryDialog.open}
        onOpenChange={(open) => setChangeCategoryDialog({ open })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alterar Categoria do Cupom</DialogTitle>
            <DialogDescription>
              O cupom <strong>{changeCategoryDialog.coupon?.baseCode}</strong> será excluído e
              recriado na nova categoria. Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Categoria Atual</label>
              <div>{getCategoryBadge(changeCategoryDialog.coupon?.category)}</div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Nova Categoria</label>
              <Select
                value={changeCategoryDialog.newCategory}
                onValueChange={(value) =>
                  setChangeCategoryDialog((prev) => ({
                    ...prev,
                    newCategory: value as CouponCategory,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a nova categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="influencer">Influenciadora oficial</SelectItem>
                  <SelectItem value="nutricionista">Nutricionista</SelectItem>
                  <SelectItem value="revendedor">Micro/Mano e Revendedores</SelectItem>
                  <SelectItem value="teste">Teste</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {changeCategoryDialog.newCategory && (
              <div className="bg-muted p-3 rounded-lg text-sm">
                <p className="font-medium mb-1">Novos cupons que serão criados:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  {changeCategoryDialog.newCategory === "influencer" && (
                    <>
                      <li>{changeCategoryDialog.coupon?.baseCode}: 15% ilimitado</li>
                      <li>{changeCategoryDialog.coupon?.baseCode}PEDIDO: R$600 (1x)</li>
                    </>
                  )}
                  {changeCategoryDialog.newCategory === "nutricionista" && (
                    <>
                      <li>{changeCategoryDialog.coupon?.baseCode}: 15% ilimitado</li>
                      <li>{changeCategoryDialog.coupon?.baseCode}PEDIDO: 25% (1x/mês)</li>
                    </>
                  )}
                  {changeCategoryDialog.newCategory === "revendedor" && (
                    <>
                      <li>{changeCategoryDialog.coupon?.baseCode}: 15% ilimitado</li>
                      <li>{changeCategoryDialog.coupon?.baseCode}PEDIDO: 20% (1x/mês)</li>
                    </>
                  )}
                  {changeCategoryDialog.newCategory === "teste" && (
                    <>
                      <li>{changeCategoryDialog.coupon?.baseCode}: 15% ilimitado</li>
                      <li>{changeCategoryDialog.coupon?.baseCode}PEDIDO: R$400 (1x)</li>
                    </>
                  )}
                </ul>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setChangeCategoryDialog({ open: false })}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleChangeCategory}
              disabled={!changeCategoryDialog.newCategory}
            >
              Confirmar Alteração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCreateCoupon;


